import React, { FunctionComponent, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'

import moment from 'moment'

import {
    faExclamationTriangle,
    faInfoCircle,
    faTrashAlt,
    faTrashRestore,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import hexToRgb from '../../../App/Util/hexToRgb'
import { ILastMinuteActivity } from '../Client/ILastMinute'
import {
    deleteLastMinute,
    deleteLastMinutePermanent,
    undoDeleteLastMinute,
} from '../Store/Actions'

import { Timer } from './Components/Timer/Timer'
import IProps from './IProps'
import style from './View.scss'

const View: FunctionComponent<IProps> = ({ lastMinute }) => {
    const [t] = useTranslation()
    const dispatch = useDispatch()
    const [isDeleted, setDeleted] = useState<boolean>(false)
    const [clicked, setClicked] = useState<boolean>(false)

    const handleDeleteClick = () => {
        setDeleted(true)
        dispatch(deleteLastMinute(lastMinute))
        setClicked(true)
    }
    useEffect(() => {
        if (clicked && isDeleted) {
            const timer = setTimeout(() => {
                dispatch(deleteLastMinutePermanent(lastMinute))
                setClicked(false)
                setDeleted(false)
            }, 12500)
            return () => clearTimeout(timer)
        }
    }, [clicked, isDeleted])

    const handleUndoDeleteClick = () => {
        setDeleted(false)
        dispatch(undoDeleteLastMinute(lastMinute))
    }

    let startsAt: moment.Moment
    lastMinute.activities.forEach((lastMinuteActivity: ILastMinuteActivity) => {
        if (
            typeof startsAt === 'undefined' ||
            startsAt.isAfter(lastMinuteActivity.startsAt)
        ) {
            startsAt = lastMinuteActivity.startsAt.clone()
        }
    })

    return (
        <div
            className={`${style.lastMinute} ${
                isDeleted ? style.deleted : null
            }`}
        >
            <div className={style.lastMinuteTitle}>
                {startsAt.format('dddd DD MMMM YYYY')}
            </div>
            <div className={style.lastMinuteActions}>
                <div onClick={handleDeleteClick}>
                    <FontAwesomeIcon icon={faTrashAlt} />
                </div>
            </div>
            {lastMinute.isBooked && (
                <div className={style.lastMinuteSuccess}>
                    <FontAwesomeIcon icon={faInfoCircle} />{' '}
                    {t('This last minute has been booked by {{user}}!', {
                        user:
                            lastMinute.appointment.customer.first_name +
                            ' ' +
                            lastMinute.appointment.customer.last_name,
                    })}
                </div>
            )}

            {lastMinute.hasOverlap && (
                <div className={style.lastMinuteWarning}>
                    <FontAwesomeIcon icon={faExclamationTriangle} />
                    {t(
                        'This last minute is overlapped with another appointment.'
                    )}
                </div>
            )}

            {lastMinute.activities.map((lastMinuteActivity, index) => {
                let color = hexToRgb(lastMinuteActivity.color)
                if (!color) {
                    color = { r: 255, g: 0, b: 0 }
                }

                const activityElement = (
                    <div
                        className={style.lastMinuteActivity}
                        key={'activity' + index}
                        style={{
                            borderColor: lastMinuteActivity.color,
                            backgroundColor: `rgba(${color.r}, ${color.g}, ${color.b}, .2)`,
                            color: lastMinuteActivity.color,
                        }}
                    >
                        <div className={style.lastMinuteActivityTime}>
                            {lastMinuteActivity.startsAt.format('HH:mm')} -
                            {lastMinuteActivity.endsAt.format('HH:mm')}
                        </div>
                        <div className={style.lastMinuteActivityName}>
                            {lastMinuteActivity.name}
                        </div>
                        <div className={style.lastMinuteActivityPerson}>
                            {lastMinuteActivity.persons
                                .map((person: any) => person.name)
                                .join(', ')}
                        </div>
                    </div>
                )

                return activityElement
            })}

            <div className={style.destructiveOverlay}>
                <p>{t('The last minute has been removed.')}</p>
                <button
                    className={style.undoButton}
                    type={'button'}
                    onClick={handleUndoDeleteClick}
                >
                    {isDeleted && <Timer />}
                    <br />
                    {t('Undo')}
                </button>
            </div>
        </div>
    )
}

export default View
